import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, MessageSquare, Star, AlertCircle, Check, Clock3, X } from 'lucide-react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_ALL_LEND_BOOKINGS } from '../graphql/queries/allLendBookings';
import { TOOL_BOOKING_THREAD } from '../graphql/queries/toolBookingThread';
import { CREATE_MESSAGE_THREAD, CREATE_MESSAGE_THREAD_INPUT } from '../graphql/mutations/createMessageThread';
import {
  RETURN_TOOL_BOOKING,
  RETURN_TOOL_BOOKING_DIRECT,
  COMPLETE_TOOL_BOOKING,
  COMPLETE_TOOL_BOOKING_DIRECT,
  RETURN_TOOL_BOOKING_WITH_DAMAGES,
} from '../graphql/mutations/returnToolBooking';
import {
  REVIEW_TOOL_BOOKING,
  REVIEW_TOOL_BOOKING_DIRECT,
  COMPLETE_TOOL_BOOKING_WITH_REVIEW,
  COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW,
} from '../graphql/mutations/reviewToolBooking';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';

const AllLendBookings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(true);
  const [messageThreadLoadingBookingId, setMessageThreadLoadingBookingId] = useState<string | null>(null);
  const [returnBooking, setReturnBooking] = useState<any>(null);
  const [returnBookingNote, setReturnBookingNote] = useState('');
  const [returnBookingLoading, setReturnBookingLoading] = useState(false);
  const [returnBookingAction, setReturnBookingAction] = useState<'clean' | 'issues' | null>(null);
  const [showReturnDamageDialog, setShowReturnDamageDialog] = useState(false);
  const [returnDamageComment, setReturnDamageComment] = useState('');
  const [returnDamageFiles, setReturnDamageFiles] = useState<File[]>([]);
  const [reviewBooking, setReviewBooking] = useState<any>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewBookingLoading, setReviewBookingLoading] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const { data, loading, error, refetch: refetchLendBookings } = useQuery(GET_ALL_LEND_BOOKINGS, {
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const bookings = data?.currentUser?.lendToolBookings?.items || [];
  const totalCount = data?.currentUser?.lendToolBookings?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Fetch message thread for selected booking
  const { data: threadData, loading: threadLoading } = useQuery(TOOL_BOOKING_THREAD, {
    variables: { id: selectedBooking?.id },
    skip: !selectedBooking?.id,
    fetchPolicy: 'network-only',
  });
  const [fetchBookingThread] = useLazyQuery(TOOL_BOOKING_THREAD, {
    fetchPolicy: 'network-only',
  });
  const [createMessageThread] = useMutation(CREATE_MESSAGE_THREAD);
  const [createMessageThreadInput] = useMutation(CREATE_MESSAGE_THREAD_INPUT);
  const [returnToolBooking] = useMutation(RETURN_TOOL_BOOKING);
  const [returnToolBookingDirect] = useMutation(RETURN_TOOL_BOOKING_DIRECT);
  const [returnToolBookingWithDamages] = useMutation(RETURN_TOOL_BOOKING_WITH_DAMAGES);
  const [completeToolBooking] = useMutation(COMPLETE_TOOL_BOOKING);
  const [completeToolBookingDirect] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT);
  const [reviewToolBooking] = useMutation(REVIEW_TOOL_BOOKING);
  const [reviewToolBookingDirect] = useMutation(REVIEW_TOOL_BOOKING_DIRECT);
  const [completeToolBookingWithReview] = useMutation(COMPLETE_TOOL_BOOKING_WITH_REVIEW);
  const [completeToolBookingDirectWithReview] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW);

  const messages = threadData?.toolBooking?.messageThread?.messages || [];

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Format price
  const formatPrice = (cents: number) => {
    const kroner = cents / 100;
    const hasDecimals = cents % 100 !== 0;
    return `${kroner.toLocaleString('da-DK', { 
      minimumFractionDigits: hasDecimals ? 2 : 0, 
      maximumFractionDigits: 2 
    })} kr`;
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
      requested: { text: 'Anmodet', color: 'bg-yellow-100 text-yellow-800', icon: Clock3 },
      declined: { text: 'Afvist', color: 'bg-red-100 text-red-800', icon: X },
      canceled: { text: 'Annulleret', color: 'bg-gray-100 text-gray-800', icon: X },
      confirmed: { text: 'Bekræftet', color: 'bg-blue-100 text-blue-800', icon: Check },
      picked_up: { text: 'Afhentet', color: 'bg-green-100 text-green-800', icon: Check },
      returned: { text: 'Returneret', color: 'bg-purple-100 text-purple-800', icon: Check },
      reviewed_by_booking_user: { text: 'Anmeldt af lejer', color: 'bg-indigo-100 text-indigo-800', icon: Star },
      reviewed_by_owner: { text: 'Afsluttet', color: 'bg-green-100 text-green-800', icon: Check },
      completed: { text: 'Afsluttet', color: 'bg-green-100 text-green-800', icon: Check },
    };
    return badges[status] || { text: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
  };

  const renderStatusBadge = (status: string) => {
    const statusBadge = getStatusBadge(status);
    const StatusIcon = statusBadge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
        <StatusIcon className="h-3.5 w-3.5" />
        <span>{statusBadge.text}</span>
      </span>
    );
  };

  const getLendBookingAction = (status: string) => {
    if (status === 'requested') {
      return {
        label: 'Håndtér',
        variant: 'primary' as const,
        action: 'details' as const,
        icon: <Clock3 className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'picked_up') {
      return {
        label: 'Bekræft retur',
        variant: 'primary' as const,
        action: 'return' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'returned' || status === 'reviewed_by_booking_user') {
      return {
        label: 'Afslut',
        variant: 'primary' as const,
        action: 'review' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    return {
      label: 'Se status',
      variant: 'outline' as const,
      action: 'details' as const,
      icon: undefined,
    };
  };

  const shouldHideLendActionButton = (status: string) =>
    status === 'confirmed' ||
    status === 'canceled' ||
    status === 'declined' ||
    status === 'completed' ||
    status === 'reviewed_by_owner';

  const getLendBadgeStatus = (status: string) => {
    if (status === 'reviewed_by_booking_user') return 'returned';
    if (status === 'reviewed_by_owner') return 'completed';
    return status;
  };

  const handleOpenBookingDialog = (booking: any) => {
    setIsMessagesCollapsed(true);
    setSelectedBooking(booking);
  };

  const handleOpenReturnDialog = (booking: any) => {
    setReturnBooking(booking);
    setReturnBookingNote('');
    setReturnBookingAction(null);
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleCloseReturnDialog = () => {
    if (returnBookingLoading) return;
    setReturnBooking(null);
    setReturnBookingNote('');
    setReturnBookingAction(null);
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleOpenReturnDamageDialog = () => {
    setShowReturnDamageDialog(true);
    setReturnDamageComment(returnBookingNote.trim());
    setReturnDamageFiles([]);
  };

  const handleCloseReturnDamageDialog = () => {
    if (returnBookingLoading) return;
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleReturnDamageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      toast.error('Kun billeder kan uploades i skadeanmeldelsen.');
    }

    if (imageFiles.length > 0) {
      setReturnDamageFiles((prev) => [...prev, ...imageFiles]);
    }

    event.target.value = '';
  };

  const handleRemoveReturnDamageFile = (index: number) => {
    setReturnDamageFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const convertFileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result) {
          reject(new Error('Kunne ikke læse filen.'));
          return;
        }
        resolve(result.split(',')[1]);
      };
      reader.onerror = () => reject(new Error('Kunne ikke læse filen.'));
      reader.readAsDataURL(file);
    });

  const handleSubmitReturnedWithDamages = async () => {
    if (!returnBooking?.id) return;

    const bookingToReview = returnBooking;

    const trimmedComment = returnDamageComment.trim();
    if (!trimmedComment) {
      toast.error('Skriv venligst en kommentar om skaden eller manglen.');
      return;
    }

    if (returnDamageFiles.length === 0) {
      toast.error('Upload mindst ét billede af skaden eller manglen.');
      return;
    }

    setReturnBookingLoading(true);
    setReturnBookingAction('issues');

    try {
      const convertedAttachments = await Promise.all(
        returnDamageFiles.map(async (file) => ({
          filename: file.name,
          contentType: file.type,
          base64: await convertFileToBase64(file),
        }))
      );

      const { data } = await returnToolBookingWithDamages({
        variables: {
          id: returnBooking.id,
          comment: trimmedComment,
          attachments: convertedAttachments,
        },
      });

      const payload = data?.toolReturnedWithDamages;
      const returnErrors = payload?.errors || [];
      if (returnErrors.length > 0) {
        toast.error(returnErrors[0]);
        return;
      }

      toast.success('Skadeanmeldelse sendt. Vores support vender tilbage hurtigst muligt.');
      setReturnBooking(null);
      setReturnBookingNote('');
      setReturnDamageComment('');
      setReturnDamageFiles([]);
      setShowReturnDamageDialog(false);
      setReturnBookingAction(null);
      setSelectedBooking(null);
      handleOpenReviewDialog(bookingToReview);
      await refetchLendBookings();
    } catch {
      toast.error('Der opstod en fejl ved registrering af skadeanmeldelsen.');
    } finally {
      setReturnBookingLoading(false);
      setReturnBookingAction(null);
    }
  };

  const resetReviewDialogState = () => {
    setReviewBooking(null);
    setReviewRating(0);
    setReviewComment('');
  };

  const handleOpenReviewDialog = (booking: any) => {
    setReviewBooking(booking);
    setReviewRating(0);
    setReviewComment('');
  };

  const handleCloseReviewDialog = () => {
    if (reviewBookingLoading) return;
    resetReviewDialogState();
  };

  const isSchemaArgumentError = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    return graphQLErrors.some((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      return code === 'missingRequiredArguments' || code === 'argumentNotAccepted' || code === 'variableNotUsed';
    });
  };

  const handleOpenBookingConversation = async (booking: any) => {
    if (!booking?.id) return;

    setMessageThreadLoadingBookingId(booking.id);

    try {
      const { data } = await fetchBookingThread({
        variables: { id: booking.id },
      });

      const threadId = data?.toolBooking?.messageThread?.id;
      if (threadId) {
        navigate(`/conversation/${threadId}`);
        return;
      }

      const toolId = booking?.tool?.id;
      if (!toolId) {
        toast.error('Kunne ikke finde værktøjet for beskeden.');
        return;
      }

      const variables = {
        toolId,
        toolBookingId: booking.id,
        participantIds: booking?.bookingUser?.id ? [booking.bookingUser.id] : undefined,
      };

      let toolThreadData: any;
      try {
        const response = await createMessageThread({ variables });
        toolThreadData = response.data;
      } catch (createThreadError) {
        if (!isSchemaArgumentError(createThreadError)) {
          throw createThreadError;
        }

        const fallbackResponse = await createMessageThreadInput({ variables });
        toolThreadData = fallbackResponse.data;
      }

      const toolThreadErrors = toolThreadData?.createMessageThread?.errors || [];
      if (toolThreadErrors.length > 0) {
        toast.error(toolThreadErrors[0]);
        return;
      }

      const fallbackThreadId = toolThreadData?.createMessageThread?.messageThread?.id;
      if (!fallbackThreadId) {
        toast.error('Kunne ikke oprette en ny beskedtråd.');
        return;
      }

      navigate(`/conversation/${fallbackThreadId}`);
    } catch {
      toast.error('Der opstod en fejl ved åbning af beskedtråden.');
    } finally {
      setMessageThreadLoadingBookingId(null);
    }
  };

  const shouldTryNextReturnMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const shouldTryNextReviewMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const handleCompleteWithoutReview = async () => {
    if (!reviewBooking?.id) return;

    setReviewBookingLoading(true);

    const note = reviewComment.trim();
    const message = note
      ? `Booking afsluttet uden bedømmelse. ${note}`
      : 'Booking afsluttet uden bedømmelse.';
    const variables = {
      id: reviewBooking.id,
      message,
    };

    try {
      const attempts = [
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = response?.data?.completeToolBooking;
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel afslutnings-mutation.');
      }

      const completeErrors = payload?.errors || [];
      if (completeErrors.length > 0) {
        toast.error(completeErrors[0]);
        return;
      }

      toast.success('Booking afsluttet uden bedømmelse.');
      resetReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afslutning af booking.');
    } finally {
      setReviewBookingLoading(false);
    }
  };

  const handleSubmitOwnerReview = async () => {
    if (!reviewBooking?.id) return;
    if (reviewRating < 1) {
      await handleCompleteWithoutReview();
      return;
    }

    setReviewBookingLoading(true);

    const comment = reviewComment.trim() || null;
    const reviewVariables = {
      id: reviewBooking.id,
      rating: reviewRating,
      comment,
    };
    const reviewAndCompleteVariables = {
      id: reviewBooking.id,
      rating: reviewRating,
      comment,
      message: 'Booking afsluttet med bedømmelse.',
    };
    const completeVariables = {
      id: reviewBooking.id,
      message: comment
        ? `Booking afsluttet med bedømmelse. ${comment}`
        : 'Booking afsluttet med bedømmelse.',
    };

    const getPayload = (data: any) => data?.reviewToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => reviewToolBooking({ variables: reviewVariables }),
        () => reviewToolBookingDirect({ variables: reviewVariables }),
        () => completeToolBookingWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBookingDirectWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBooking({ variables: completeVariables }),
        () => completeToolBookingDirect({ variables: completeVariables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel bedømmelses-mutation.');
      }

      const reviewErrors = payload?.errors || [];
      if (reviewErrors.length > 0) {
        toast.error(reviewErrors[0]);
        return;
      }

      toast.success('Bedømmelse gemt og booking afsluttet.');
      resetReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afsendelse af bedømmelsen.');
    } finally {
      setReviewBookingLoading(false);
    }
  };

  const handleMarkReturned = async (hasIssues: boolean) => {
    if (!returnBooking?.id) return;

    const bookingToReview = returnBooking;

    setReturnBookingLoading(true);
    setReturnBookingAction(hasIssues ? 'issues' : 'clean');

    const baseMessage = hasIssues
      ? 'Værktøjet er returneret med skader eller mangler.'
      : 'Værktøjet er returneret.';

    const detailsMessage = returnBookingNote.trim();
    const message = detailsMessage ? `${baseMessage} ${detailsMessage}` : baseMessage;
    const variables = {
      id: returnBooking.id,
      message,
    };

    const getPayload = (data: any) => data?.returnToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => returnToolBooking({ variables }),
        () => returnToolBookingDirect({ variables }),
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReturnMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel returnerings-mutation.');
      }

      const returnErrors = payload?.errors || [];
      if (returnErrors.length > 0) {
        toast.error(returnErrors[0]);
        return;
      }

      toast.success(hasIssues ? 'Returnering med skader/mangler registreret.' : 'Værktøjet er markeret som returneret.');
      setReturnBooking(null);
      setReturnBookingNote('');
      setReturnBookingAction(null);
      setSelectedBooking(null);
      handleOpenReviewDialog(bookingToReview);
      await refetchLendBookings();
    } catch {
      toast.error('Der opstod en fejl ved registrering af returnering.');
    } finally {
      setReturnBookingLoading(false);
      setReturnBookingAction(null);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Tilbage til dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Alle udlån</h1>
          <p className="mt-2 text-gray-600">Oversigt over alle dine udlån</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Indlæser...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">Der opstod en fejl ved indlæsning af udlån</p>
            </div>
          ) : bookings.length > 0 ? (
            <>
              <div className="space-y-4">
                {bookings.map((booking: any) => {
                  const actionButton = getLendBookingAction(booking.status);
                  return (
                    <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
                      <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                        <img 
                          src={booking.tool.primaryImageUrl} 
                          alt={booking.tool.name} 
                          className="h-full w-full object-cover" 
                        />
                      </div>
                      <div className="flex-1 p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                {booking.tool.name}
                              </Link>
                              {booking.totalPriceCents && (
                                <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                  {formatPrice(booking.totalPriceCents)}
                                </div>
                              )}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                              </span>
                              {renderStatusBadge(getLendBadgeStatus(booking.status))}
                            </div>
                            <div className="mt-2 flex items-start gap-2">
                              <Link
                                to={`/profile/${booking.bookingUser.id}`}
                                className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                              >
                                {booking.bookingUser.avatarImageUrl ? (
                                  <img
                                    src={booking.bookingUser.avatarImageUrl}
                                    alt={booking.bookingUser.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span>{booking.bookingUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                                )}
                              </Link>
                              <div className="text-sm leading-tight">
                                <p className="text-gray-500">
                                  Lejet af:{' '}
                                  <Link to={`/profile/${booking.bookingUser.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                    {booking.bookingUser.name}
                                  </Link>
                                </p>
                                <Link
                                  to={`/profile/${booking.bookingUser.id}?tab=reviews`}
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                >
                                  <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                  <span>
                                    {booking.bookingUser.ratingAverage !== null && booking.bookingUser.reviewCount > 0
                                      ? booking.bookingUser.ratingAverage.toFixed(1)
                                      : 'Ingen anmeldelser'}
                                  </span>
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                            {booking.totalPriceCents && (
                              <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                {formatPrice(booking.totalPriceCents)}
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              icon={<MessageSquare className="h-4 w-4 mr-2" />}
                              isLoading={messageThreadLoadingBookingId === booking.id}
                              onClick={() => handleOpenBookingConversation(booking)}
                            >
                              Send besked
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              onClick={() => handleOpenBookingDialog(booking)}
                            >
                              Detaljer
                            </Button>
                            {!shouldHideLendActionButton(booking.status) && (
                              <Button
                                variant={actionButton.variant}
                                size="sm"
                                fullWidth
                                icon={actionButton.icon}
                                onClick={() => {
                                  if (actionButton.action === 'return') {
                                    handleOpenReturnDialog(booking);
                                    return;
                                  }
                                  if (actionButton.action === 'review') {
                                    handleOpenReviewDialog(booking);
                                    return;
                                  }
                                  handleOpenBookingDialog(booking);
                                }}
                              >
                                {actionButton.label}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Viser {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} af {totalCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Side {currentPage} af {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Ingen udlån fundet</p>
            </div>
          )}
        </div>
      </div>

      {/* Booking Details Dialog */}
      {selectedBooking && (
        <Dialog
          isOpen={!!selectedBooking}
          onClose={() => setSelectedBooking(null)}
          title="Booking detaljer"
          size="lg"
        >
          <div className="p-6">
            {/* Tool info */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Værktøj</h3>
              <div className="flex gap-4">
                <img 
                  src={selectedBooking.tool.primaryImageUrl} 
                  alt={selectedBooking.tool.name}
                  className="w-24 h-24 rounded-lg object-cover"
                />
                <div>
                  <h4 className="font-medium text-gray-900">{selectedBooking.tool.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{selectedBooking.tool.description}</p>
                </div>
              </div>
            </div>

            {/* Booking period */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Lejeperiode</h3>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
                <p className="text-gray-900">
                  {formatDate(selectedBooking.fromDate)} - {formatDate(selectedBooking.toDate)}
                </p>
                {renderStatusBadge(getLendBadgeStatus(selectedBooking.status))}
              </div>
            </div>

            {/* Price */}
            {selectedBooking.totalPriceCents && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Pris</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {formatPrice(selectedBooking.totalPriceCents)}
                </p>
              </div>
            )}

            {/* Renter info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Lejer information</h3>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium text-lg overflow-hidden">
                  {selectedBooking.bookingUser.avatarImageUrl ? (
                    <img 
                      src={selectedBooking.bookingUser.avatarImageUrl} 
                      alt={selectedBooking.bookingUser.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span>{selectedBooking.bookingUser.name?.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedBooking.bookingUser.name}</p>
                  {selectedBooking.bookingUser.reviewCount > 0 ? (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span>{selectedBooking.bookingUser.ratingAverage?.toFixed(1)}</span>
                      <span className="text-gray-400">({selectedBooking.bookingUser.reviewCount} anmeldelser)</span>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Ingen anmeldelser endnu</p>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="mb-6">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 mb-3 text-left text-gray-700 hover:text-gray-900"
                onClick={() => setIsMessagesCollapsed((collapsed) => !collapsed)}
                aria-label={isMessagesCollapsed ? 'Expandér beskeder' : 'Kollaps beskeder'}
              >
                <h3 className="text-sm font-medium">Beskeder</h3>
                {isMessagesCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>

              {!isMessagesCollapsed && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
                  {threadLoading ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Indlæser beskeder...</p>
                    </div>
                  ) : messages.length > 0 ? (
                    messages.map((message: any) => {
                      const isOwner = message.sender.id === user?.id;
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isOwner ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-xs px-4 py-2 rounded-lg ${
                            isOwner
                              ? 'bg-teal-600 text-white'
                              : 'bg-white text-gray-900 border border-gray-200'
                          }`}>
                            <p className="text-sm">{message.content}</p>
                            <p className={`text-xs mt-1 ${
                              isOwner ? 'text-teal-100' : 'text-gray-400'
                            }`}>
                              {formatDate(message.sentAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Ingen beskeder endnu</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Close button */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setSelectedBooking(null)}>
                Luk
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <Dialog
        isOpen={!!returnBooking && !showReturnDamageDialog}
        onClose={handleCloseReturnDialog}
        title="Er værktøj returneret?"
        size="md"
      >
        {returnBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Vælg hvordan returneringen skal registreres for denne booking.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{returnBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(returnBooking.fromDate)} - {formatDate(returnBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (valgfri)
              </label>
              <textarea
                value={returnBookingNote}
                onChange={(event) => setReturnBookingNote(event.target.value)}
                placeholder="Tilføj evt. detaljer om returneringen..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={returnBookingLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReturnDialog}
                disabled={returnBookingLoading}
              >
                Annuller
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={handleOpenReturnDamageDialog}
                isLoading={returnBookingLoading && returnBookingAction === 'issues'}
                disabled={returnBookingLoading}
              >
                Returneret med skader/mangler
              </Button>
              <Button
                variant="primary"
                onClick={() => handleMarkReturned(false)}
                isLoading={returnBookingLoading && returnBookingAction === 'clean'}
                disabled={returnBookingLoading}
              >
                Værktøj returneret
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!returnBooking && showReturnDamageDialog}
        onClose={handleCloseReturnDamageDialog}
        title="Rapportér skader/mangler"
        size="md"
      >
        {returnBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Beskriv skaden eller manglen og upload billeder som dokumentation.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Vores support kigger på sagen og vender tilbage hurtigst muligt.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{returnBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(returnBooking.fromDate)} - {formatDate(returnBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kommentar
              </label>
              <textarea
                value={returnDamageComment}
                onChange={(event) => setReturnDamageComment(event.target.value)}
                placeholder="Beskriv skaden eller manglen..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={returnBookingLoading}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billeder (mindst 1)
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <input
                  id="return-damage-files-all-lend"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleReturnDamageFileSelect}
                  disabled={returnBookingLoading}
                  className="sr-only"
                />
                <label
                  htmlFor="return-damage-files-all-lend"
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium w-fit ${
                    returnBookingLoading
                      ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                      : 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Vælg billeder
                </label>
                <span className="text-sm text-gray-600">
                  {returnDamageFiles.length > 0
                    ? `${returnDamageFiles.length} fil${returnDamageFiles.length > 1 ? 'er' : ''} valgt`
                    : 'Ingen filer valgt'}
                </span>
              </div>
            </div>

            {returnDamageFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {returnDamageFiles.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                    <p className="text-sm text-gray-700 truncate pr-3">{file.name}</p>
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-700"
                      onClick={() => handleRemoveReturnDamageFile(index)}
                      disabled={returnBookingLoading}
                    >
                      Fjern
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReturnDamageDialog}
                disabled={returnBookingLoading}
              >
                Tilbage
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitReturnedWithDamages}
                isLoading={returnBookingLoading && returnBookingAction === 'issues'}
                disabled={
                  returnBookingLoading ||
                  !returnDamageComment.trim() ||
                  returnDamageFiles.length === 0
                }
              >
                Send til support
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!reviewBooking}
        onClose={handleCloseReviewDialog}
        title="Afslut udlån"
        size="md"
      >
        {reviewBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Giv bedømmelse af lejeren og afslut bookingen.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{reviewBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(reviewBooking.fromDate)} - {formatDate(reviewBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Antal stjerner (valgfri)</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReviewRating(value)}
                    className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={reviewBookingLoading}
                    aria-label={`${value} stjerner`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        value <= reviewRating ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {reviewRating > 0 ? `${reviewRating} ud af 5 stjerner valgt` : 'Ingen stjerner valgt endnu'}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kommentar (valgfri)</label>
              <textarea
                value={reviewComment}
                onChange={(event) => setReviewComment(event.target.value)}
                placeholder="Skriv en kommentar til lejeren..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={reviewBookingLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReviewDialog}
                disabled={reviewBookingLoading}
              >
                Annuller
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitOwnerReview}
                isLoading={reviewBookingLoading}
                disabled={reviewBookingLoading}
              >
                Afslut
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AllLendBookings;
